import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';

/**
 * Sprint A — Correct-answer editor for question editor.
 *
 *   variant="single"  → RadioGroup over option ids + explicit "No correct
 *                       answer" entry. Selecting one option sets score=1 for
 *                       that option and 0 for every other; selecting None
 *                       sets all to 0.
 *   variant="multi"   → One Checkbox per option labelled "Correct" — checked
 *                       options get score=1, unchecked get 0.
 *
 * The UI never exposes raw `score` values.
 */
const NONE = '__NONE__';

export function CorrectAnswerRadio({ variant, options, onChange, testIdPrefix }) {
  const { t } = useTranslation();
  const isSingle = variant === 'single';

  const value = useMemo(() => {
    if (!isSingle) return null;
    const correct = (options || []).find((o) => Number(o.score) >= 1);
    return correct ? correct.id : NONE;
  }, [isSingle, options]);

  if (isSingle) {
    return (
      <div className="space-y-2" data-testid={`${testIdPrefix}-single`}>
        <p className="text-xs uppercase tracking-wider font-semibold text-muted-foreground">
          {t('library.correctAnswer.titleSingle', { defaultValue: 'Mark the correct answer' })}
        </p>
        <RadioGroup
          value={value || NONE}
          onValueChange={(next) => {
            const newOptions = (options || []).map((o) => ({
              ...o,
              score: next === o.id ? 1 : 0,
            }));
            onChange(newOptions);
          }}
          className="space-y-1.5"
        >
          {(options || []).map((o) => (
            <div key={o.id} className="flex items-center gap-2 text-xs">
              <RadioGroupItem
                value={o.id}
                id={`${testIdPrefix}-opt-${o.id}`}
                data-testid={`${testIdPrefix}-radio-${o.id}`}
              />
              <Label htmlFor={`${testIdPrefix}-opt-${o.id}`} className="cursor-pointer">
                {o.label || <span className="italic text-muted-foreground">(no label)</span>}
              </Label>
            </div>
          ))}
          <div className="flex items-center gap-2 text-xs pt-1 border-t border-dashed">
            <RadioGroupItem
              value={NONE}
              id={`${testIdPrefix}-opt-none`}
              data-testid={`${testIdPrefix}-radio-none`}
            />
            <Label htmlFor={`${testIdPrefix}-opt-none`} className="cursor-pointer italic text-muted-foreground">
              {t('library.correctAnswer.noneOption', { defaultValue: 'This evaluation has no correct answer' })}
            </Label>
          </div>
        </RadioGroup>
      </div>
    );
  }

  // multi-choice
  return (
    <div className="space-y-2" data-testid={`${testIdPrefix}-multi`}>
      <p className="text-xs uppercase tracking-wider font-semibold text-muted-foreground">
        {t('library.correctAnswer.titleMulti', { defaultValue: 'Mark all correct answers' })}
      </p>
      <div className="space-y-1.5">
        {(options || []).map((o) => (
          <div key={o.id} className="flex items-center gap-2 text-xs">
            <Checkbox
              id={`${testIdPrefix}-cb-${o.id}`}
              checked={Number(o.score) >= 1}
              onCheckedChange={(checked) => {
                const newOptions = (options || []).map((x) =>
                  x.id === o.id ? { ...x, score: checked ? 1 : 0 } : x,
                );
                onChange(newOptions);
              }}
              data-testid={`${testIdPrefix}-checkbox-${o.id}`}
            />
            <Label htmlFor={`${testIdPrefix}-cb-${o.id}`} className="cursor-pointer">
              {o.label || <span className="italic text-muted-foreground">(no label)</span>}
            </Label>
          </div>
        ))}
      </div>
    </div>
  );
}

export default CorrectAnswerRadio;
